import { useEffect, useMemo } from 'react';
import {
  useProducts,
  useBundles,
  useResolvedProductPrices,
  useResolvedBundlePrices,
  useResolvedSlotPrices,
} from '@/hooks/queries';
import { useCartStore } from '@/stores/cart.store';
import { collectSlotPairs } from '@/lib/slotPricing';

const EMPTY_SLOT_PRICES: Record<string, number> = {};

/**
 * Keeps displayed prices in sync with what the sale will actually charge.
 *
 * Catalog base prices are weekday defaults; the real price depends on the visit
 * date (weekend / holiday / school-holiday PricingSchedule rules). This hook
 * resolves date-aware prices for every catalog product + bundle (and anything
 * already in the cart) and reprices the cart whenever those maps arrive/change
 * (e.g. the cashier picks a different date) or a new item is added.
 *
 * Cart prices are display-only — the BE re-resolves authoritatively at sale
 * time — so this only affects what the cashier sees, never the charge.
 *
 * Returns the resolved maps so the caller can render date-aware prices on
 * catalog cards and pass them into the cart at add time (avoiding a base→resolved
 * flash). Call it on the catalog (date changes) and details (attraction adds).
 */
export function usePriceSync() {
  const visitDate = useCartStore((s) => s.visitDate);
  const repriceCart = useCartStore((s) => s.repriceCart);
  const lines = useCartStore((s) => s.lines);
  const bundleLines = useCartStore((s) => s.bundleLines);

  const { data: productData } = useProducts();
  const { data: bundleData } = useBundles();

  // Every product id we might price: all catalog products (main + attraction)
  // plus anything already in the cart (line, seat add-on, bundle extra).
  const productIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of productData?.items ?? []) set.add(p.id);
    for (const l of lines) {
      set.add(l.productId);
      for (const seat of l.seats) for (const a of seat.addOns) set.add(a.productId);
    }
    for (const b of bundleLines) for (const a of b.extraAddOns) set.add(a.productId);
    return Array.from(set);
  }, [productData, lines, bundleLines]);

  const bundleIds = useMemo(() => {
    const set = new Set<string>();
    for (const b of bundleData ?? []) set.add(b.id);
    for (const b of bundleLines) set.add(b.bundleId);
    return Array.from(set);
  }, [bundleData, bundleLines]);

  const { data: productPrices } = useResolvedProductPrices(productIds, visitDate);
  const { data: bundlePrices } = useResolvedBundlePrices(bundleIds, visitDate);

  // Every (product, slot) the cart has picked, priced against that slot — a
  // time-of-day rate (Go-Kart 18:00–23:00) prices a 19:00 slot differently
  // from the slot running now, which is what the date-only map above uses.
  const slotPairs = useMemo(() => collectSlotPairs(lines, bundleLines), [lines, bundleLines]);
  const slotQuery = useResolvedSlotPrices(slotPairs, visitDate);
  // With no slotted items there is nothing to wait for; otherwise undefined
  // while loading, which tells repriceCart to keep slotted prices as they are.
  const slotPrices = slotPairs.length === 0 ? EMPTY_SLOT_PRICES : slotQuery.data;

  // A price-free signature of the cart's item set. Repricing changes prices,
  // never ids/counts/slots, so it can't feed back into this effect (no loop).
  // The effect re-runs on: resolved maps changing (date), an item added/removed,
  // or a slot picked.
  const cartSig = useMemo(() => {
    const parts: string[] = [];
    for (const l of lines) {
      parts.push(`L${l.productId}x${l.seats.length}`);
      for (const seat of l.seats) {
        parts.push(`s${seat.slotId ?? ''}`);
        for (const a of seat.addOns) parts.push(`a${a.productId}@${a.slotId ?? ''}`);
      }
    }
    for (const b of bundleLines) {
      parts.push(`B${b.bundleId}`);
      for (const a of b.extraAddOns) parts.push(`e${a.productId}@${a.slotId ?? ''}`);
    }
    return parts.join('|');
  }, [lines, bundleLines]);

  useEffect(() => {
    if (!productPrices && !bundlePrices && !slotPrices) return;
    repriceCart(productPrices ?? {}, bundlePrices ?? {}, slotPrices);
    // cartSig re-runs the reprice when items or slots change; maps re-run it on date change.
  }, [productPrices, bundlePrices, slotPrices, cartSig, repriceCart]);

  return {
    productPrices: productPrices ?? {},
    bundlePrices: bundlePrices ?? {},
  };
}
